"use client";

import {
  normalizePhoneNumber,
  normalizePhoneDigits,
  isValidPhoneNumber,
} from "@/libs/phone";

import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import "intl-tel-input/styles";
import Select from "react-select";
import { City, ICity } from "country-state-city";
import iranCity from "iran-city";
import { ArrowPinkSVG } from "@/components/ui/SVG";
import { pickLocalizedField } from "@/utils/userLocalization";

type ContactConsultationFormProps = {
  title?: string;
  locale?: "en" | "fa";
  source?: string;
  metadata?: Record<string, unknown> | null;
  onSubmitted?: () => void;
};

type CityOption = { label: string; value: string };

const filterTextByLocale = (value: string, locale: "en" | "fa") => {
  const allowedPattern =
    locale === "fa"
      ? /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u0660-\u0669\u06F0-\u06F9\s.,،؛:;!؟?'"()[\]\-_/]/g
      : /[A-Za-z0-9\s.,:;!?'"()[\]\-_/]/g;
  return value.match(allowedPattern)?.join("") ?? "";
};

export default function ContactConsultationForm({
  title = "Request consultation and call",
  locale = "en",
  source = "contact_us",
  metadata = null,
  onSubmitted,
}: ContactConsultationFormProps) {
  const { data: session, status } = useSession();
  const isSignedIn = status === "authenticated";
  const sessionFirstName = useMemo(
    () =>
      pickLocalizedField(
        locale,
        session?.user?.name_en ?? null,
        session?.user?.name_fa ?? null,
      ),
    [locale, session?.user?.name_en, session?.user?.name_fa],
  );
  const sessionLastName = useMemo(
    () =>
      pickLocalizedField(
        locale,
        session?.user?.sur_name_en ?? null,
        session?.user?.sur_name_fa ?? null,
      ),
    [locale, session?.user?.sur_name_en, session?.user?.sur_name_fa],
  );

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [gender, setGender] = useState<"" | "MALE" | "FEMALE">("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState<string | null>("IR");
  const [cityOptions, setCityOptions] = useState<CityOption[]>([]);
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isSignedIn) return;
    setFirstName((prev) => prev || sessionFirstName);
    setLastName((prev) => prev || sessionLastName);
    setEmail((prev) => prev || session?.user?.email || "");
  }, [isSignedIn, session?.user?.email, sessionFirstName, sessionLastName]);

  const t =
    locale === "fa"
      ? {
          firstName: "نام",
          lastName: "نام خانوادگی",
          email: "پست الکترونیک",
          company: "شرکت / سازمان (اختیاری)",
          gender: "جنسیت",
          male: "آقا",
          female: "خانم",
          phone: "شماره تماس",
          cityPlaceholder: "انتخاب شهر",
          message: "سوال یا درخواست شما...",
          submit: "ارسال",
          success: "درخواست شما با موفقیت ثبت شد.",
          error: "ثبت درخواست انجام نشد. لطفا دوباره تلاش کنید.",
          knownInfo: "اطلاعات حساب شما برای این درخواست استفاده می‌شود.",
        }
      : {
          firstName: "First name",
          lastName: "Last name",
          email: "Your email",
          company: "Company / organization (optional)",
          gender: "Gender",
          male: "Male",
          female: "Female",
          phone: "Phone number",
          cityPlaceholder: "Select city",
          message: "How can we help?",
          submit: "Submit",
          success: "Your request has been submitted successfully.",
          error: "Could not submit your request. Please try again.",
          knownInfo: "Your account details will be used for this request.",
        };

  useEffect(() => {
    let iti: any = null;
    let destroyed = false;
    let handleChange: (() => void) | null = null;
    const inputEl = phoneInputRef.current;

    const init = async () => {
      if (!inputEl || destroyed) return;
      if (inputEl.closest(".iti")) return;

      const intlTelInput = (await import("intl-tel-input")).default;

      iti = intlTelInput(inputEl, {
        initialCountry: "ir",
        loadUtils: () => import("intl-tel-input/utils"),
        nationalMode: false,
        autoPlaceholder: "aggressive",
        separateDialCode: true,
      });

      handleChange = () => {
        if (!iti || !inputEl) return;
        const countryData = iti.getSelectedCountryData();
        const rawValue = normalizePhoneDigits(inputEl.value);
        setPhone(normalizePhoneNumber(rawValue, countryData?.iso2) ?? rawValue);
        if (countryData?.iso2) {
          setCountryCode(countryData.iso2.toUpperCase());
        }
      };

      handleChange();
      inputEl.addEventListener("input", handleChange);
      inputEl.addEventListener("countrychange", handleChange as EventListener);
    };

    void init();

    return () => {
      destroyed = true;
      if (inputEl && handleChange) {
        inputEl.removeEventListener("input", handleChange);
        inputEl.removeEventListener(
          "countrychange",
          handleChange as EventListener,
        );
      }
      if (iti) iti.destroy();
    };
  }, []);

  useEffect(() => {
    async function loadCities() {
      if (!countryCode) return;
      try {
        setSelectedCity(null);
        let cities: { name: string }[] | ICity[] | undefined;
        if (countryCode === "IR") {
          cities = iranCity.allCities();
        } else {
          cities = await City.getCitiesOfCountry(countryCode);
        }
        setCityOptions(
          (cities || []).map((city) => ({
            value: city.name,
            label: city.name,
          })),
        );
      } catch (err) {
        console.error("Failed to load cities:", err);
      }
    }
    void loadCities();
  }, [countryCode]);

  const clearForm = () => {
    setPhone("");
    setCompany("");
    setGender("");
    setMessage("");
    setSelectedCity(null);
    if (!isSignedIn) {
      setFirstName("");
      setLastName("");
      setEmail("");
    }
    if (phoneInputRef.current) {
      phoneInputRef.current.value = "";
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitMessage(null);
    if (!isValidPhoneNumber(phone, countryCode)) {
      setSubmitMessage({
        type: "error",
        text:
          locale === "fa"
            ? "شماره تلفن معتبر وارد کنید."
            : "Enter a valid phone number.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          company,
          gender,
          phone: normalizePhoneNumber(phone, countryCode),
          city: selectedCity?.value ?? null,
          countryCode,
          message,
          source,
          metadata,
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.error || t.error);
      }

      clearForm();
      setSubmitMessage({ type: "success", text: t.success });
      onSubmitted?.();
    } catch (error: any) {
      setSubmitMessage({ type: "error", text: error?.message || t.error });
    } finally {
      setSubmitting(false);
    }
  };

  const hasLockedFirstName = isSignedIn && Boolean(sessionFirstName);
  const hasLockedLastName = isSignedIn && Boolean(sessionLastName);
  const hasLockedEmail = isSignedIn && Boolean(session?.user?.email);

  return (
    <div dir={locale === "en" ? "ltr" : "rtl"}>
      <h3 className="relative text-xl font-semibold lg:text-2xl">
        {title}
        <ArrowPinkSVG className="absolute bottom-0 h-3 w-3 ltr:-left-3 rtl:-right-3 rtl:-rotate-90" />
      </h3>

      <form onSubmit={onSubmit} className="mt-4 grid gap-3">
        <div className="flex items-center justify-between gap-2">
          <input
            className="w-full rounded border border-gray-300 px-3 py-2 focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
            placeholder={t.firstName}
            value={firstName}
            disabled={hasLockedFirstName}
            onChange={(e) =>
              setFirstName(filterTextByLocale(e.target.value, locale))
            }
          />
          <input
            className="w-full rounded border border-gray-300 px-3 py-2 focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
            placeholder={t.lastName}
            value={lastName}
            disabled={hasLockedLastName}
            onChange={(e) =>
              setLastName(filterTextByLocale(e.target.value, locale))
            }
          />
        </div>

        <input
          className="rounded border border-gray-300 px-3 py-2 focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
          placeholder={t.email}
          value={email}
          disabled={hasLockedEmail}
          onChange={(e) => setEmail(e.target.value)}
        />

        <input
          className="rounded border border-gray-300 px-3 py-2 focus:outline-none"
          placeholder={t.company}
          value={company}
          onChange={(e) =>
            setCompany(filterTextByLocale(e.target.value, locale))
          }
        />

        <fieldset className="rounded border border-gray-300 px-3 py-2">
          <legend className="px-1 text-sm text-gray-700">{t.gender}</legend>
          <div className="flex items-center gap-6">
            {(locale === "fa"
              ? [
                  { value: "FEMALE" as const, label: t.female },
                  { value: "MALE" as const, label: t.male },
                ]
              : [
                  { value: "MALE" as const, label: t.male },
                  { value: "FEMALE" as const, label: t.female },
                ]
            ).map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2"
              >
                <input
                  type="radio"
                  name="gender"
                  value={option.value}
                  checked={gender === option.value}
                  required
                  onChange={() => setGender(option.value)}
                  className="accent-pinkCustom h-4 w-4"
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="w-full md:w-1/2">
            <input
              ref={phoneInputRef}
              type="tel"
              placeholder={t.phone}
              className="w-full rounded-sm border border-gray-300 p-2 focus:outline-none"
            />
          </div>
          <div className="w-full md:w-1/2">
            <Select
              options={cityOptions}
              value={selectedCity}
              onChange={(option) => setSelectedCity(option)}
              isDisabled={!countryCode}
              isRtl={countryCode === "IR"}
              placeholder={t.cityPlaceholder}
            />
          </div>
        </div>

        <textarea
          className="rounded border border-gray-300 px-3 py-2 focus:outline-none"
          placeholder={t.message}
          rows={4}
          value={message}
          onChange={(e) =>
            setMessage(filterTextByLocale(e.target.value, locale))
          }
        />

        {submitMessage ? (
          <p
            className={
              submitMessage.type === "success"
                ? "text-sm text-green-700"
                : "text-sm text-red-700"
            }
          >
            {submitMessage.text}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          className="bg-slateCustom rounded px-4 py-2 text-sm text-white disabled:opacity-60"
        >
          {submitting ? "..." : t.submit}
        </button>
      </form>
    </div>
  );
}
